import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { HeroSection } from '@/components/HeroSection';
import { FeaturedPosts } from '@/components/FeaturedPosts';
import { LinkedInIcon } from '@/components/icons/LinkedInIcon';
import { XIcon } from '@/components/icons/XIcon';
import { GitHubIcon } from '@/components/icons/GitHubIcon';
import { InstagramIcon } from '@/components/icons/InstagramIcon';
import { useChat } from '@/contexts/ChatContext';
import { isDesktop } from '@/lib/utils';
import mixpanel from 'mixpanel-browser';

export function HomePage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { openChat } = useChat();
  const images = [
    "https://i.imgur.com/ModwDDa.png",
    "https://i.imgur.com/Gv3Svzy.png"
  ];

  useEffect(() => {
    mixpanel.track('Website visits', { page: 'home-page' });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  // Auto-open chat on desktop after 4 seconds, but only once per session
  useEffect(() => {
    if (isDesktop() && !sessionStorage.getItem('chatAutoOpened')) {
      const timer = setTimeout(() => {
        openChat();
        sessionStorage.setItem('chatAutoOpened', 'true');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [openChat]);

  const socialNetworks = [
    {
      name: "LinkedIn",
      description: "+21,000 followers",
      icon: LinkedInIcon,
      url: "https://www.linkedin.com/in/jorge-alonso-df/",
      color: "text-[#0A66C2]"
    },
    {
      name: "X (Twitter)",
      description: "+1,200 followers",
      icon: XIcon,
      url: "",
      color: "text-[#000000] dark:text-[#FFFFFF]"
    },
    {
      name: "GitHub",
      description: "+300 commits",
      icon: GitHubIcon,
      url: "https://github.com/Jorgealonsodefrutos",
      color: "text-[#181717]"
    },
    {
      name: "Instagram",
      description: "+9,000 followers",
      icon: InstagramIcon,
      url: "https://www.instagram.com/jorgealonsodf",
      color: "text-[#E4405F]"
    },
  ];

  return (
    <div>
      <HeroSection />
      
      {/* About Me Section */}
      <section className="py-16 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 to-purple-500/10 rounded-2xl transform -rotate-3"></div>
              <img
                src={images[currentImageIndex]}
                alt="Jorge at startup event"
                className="relative rounded-2xl shadow-xl w-full h-[400px] object-cover transition-opacity duration-500"
                key={currentImageIndex}
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">👋 Hey, I'm Jorge, great to see you here!</h2>
              <p className="text-lg text-muted-foreground mb-6">
                I'm a passionate product manager (intrapreneur & entrepreneur) based in Europe. I love exploring the intersection of technology, business, and travel.
              </p>
              <p className="text-lg text-muted-foreground mb-6">
                Through this blog, I share my experiences in AI development, startup journey, and adventures around the world. Join me as I document my learnings and discoveries.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/blog?category=ai" className="flex-1 sm:flex-none">
                  <Button className="w-full sm:w-auto">Read My Articles</Button>
                </Link>
                <Link to="/traveling" className="flex-1 sm:flex-none">
                  <Button variant="outline" className="w-full sm:w-auto">View Travel Map</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Manifesto Section */}
      <section className="py-16 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">My Manifesto</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Ikigai is a Japanese concept that means "a reason for being." It comes from Okinawa and reflects the idea of living with purpose. It's found at the crossroads of what you love, what you're good at, what the world needs, and what you can be paid for. More than a career path, it's about <strong>meaning and balance.</strong>
              </p>
              <p className="text-lg text-muted-foreground mb-6">
                My ikigai lives at the <strong>intersection of tech and startups—both as an entrepreneur and intrapreneur</strong>. I discovered it when I was 14, the day my parents brought home our first computer. That moment sparked a deep <strong>curiosity</strong> that turned into a lifelong drive to build, explore, and create through technology.
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-green-500/10 to-blue-500/10 rounded-2xl transform rotate-3"></div>
              <img
                src="/manifesto-image.png"
                alt="Ikigai concept visualization"
                className="relative rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <FeaturedPosts />
      
      {/* Social Networks Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Let's Connect!</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {socialNetworks.map((social) => (
              <a 
                key={social.name} 
                href={social.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block group"
              >
                <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="text-center">
                    <div className="flex flex-col items-center gap-3">
                      <social.icon className={`h-6 w-6 ${social.color}`} />
                      <CardTitle>{social.name}</CardTitle>
                    </div>
                    <CardDescription className="text-center">
                      {social.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="ghost" 
                      className="w-full group-hover:bg-accent"
                    >
                      Follow Me
                    </Button>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}